import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useMemo } from 'react';
import en from './locales/en.json';
import he from './locales/he.json';

export type Locale = 'en' | 'he';
export type MessageParams = Record<string, string | number>;
export type Translator = (key: string, params?: MessageParams) => string;
const dictionaries: Record<Locale, Record<string, string>> = { en, he };
const LocaleContext = createContext<Locale | null>(null);

export function resolveLocale(): Locale {
  return typeof navigator !== 'undefined' && navigator.language.toLowerCase().startsWith('he')
    ? 'he'
    : 'en';
}

// Catalogs are bundled: translation management downtime cannot affect rendering.
export function translate(
  key: string,
  params: MessageParams = {},
  locale = resolveLocale(),
): string {
  const category =
    typeof params.count === 'number' ? new Intl.PluralRules(locale).select(params.count) : null;
  const keys = category ? [`${key}.${category}`, `${key}.other`, key] : [key];
  const find = (dictionary: Record<string, string>) =>
    keys.map((k) => dictionary[k]).find((v) => v?.trim());
  const message = find(dictionaries[locale]) ?? find(en) ?? en['billing.resource.error'];
  return message.replace(/\{(\w+)\}/g, (match, name: string) =>
    Object.hasOwn(params, name) ? String(params[name]) : match,
  );
}

export function LocaleProvider({
  children,
  locale: preferredLocale,
}: {
  children: ReactNode;
  locale?: Locale;
}) {
  const locale = useMemo(() => preferredLocale ?? resolveLocale(), [preferredLocale]);
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = locale === 'he' ? 'rtl' : 'ltr';
    document.title = translate('billing.app.title', {}, locale);
  }, [locale]);
  return <LocaleContext.Provider value={locale}>{children}</LocaleContext.Provider>;
}

export function useT(): Translator {
  const locale = useContext(LocaleContext) ?? resolveLocale();
  return useMemo(() => (key, params) => translate(key, params, locale), [locale]);
}

export class LocalizedError extends Error {
  constructor(
    readonly key: string,
    readonly params: MessageParams = {},
  ) {
    super(translate(key, params));
  }
}

export function userError(error: unknown, t: Translator = translate): string {
  if (error instanceof LocalizedError) return t(error.key, error.params);
  const status =
    error && typeof error === 'object' && 'statusCode' in error ? error.statusCode : null;
  const key =
    status === 401
      ? 'billing.error.unauthorized'
      : status === 403
        ? 'billing.error.forbidden'
        : status === 404
          ? 'billing.error.notFound'
          : status === 409
            ? 'billing.error.conflict'
            : status === 400 || status === 422
              ? 'billing.error.validation'
              : 'billing.error.request';
  return t(key);
}
